/** Scan ledger, signal change log, and active signal persistence (shared client + api/scan.js). */

export const SCAN_LEDGER_CAP = 2000;
export const SIGNAL_CHANGES_CAP = 500;
export const DEDUPE_MS = 60 * 60 * 1000;
export const ACTIVE_SIGNAL_TRADING_DAYS = 5;

const US_MARKET_HOLIDAYS = new Set([
  "2025-01-01", "2025-01-20", "2025-02-17", "2025-04-18", "2025-05-26",
  "2025-06-19", "2025-07-04", "2025-09-01", "2025-11-27", "2025-12-25",
  "2026-01-01", "2026-01-19", "2026-02-16", "2026-04-03", "2026-05-25",
  "2026-06-19", "2026-07-03", "2026-09-07", "2026-11-26", "2026-12-25",
]);

export function tradingDaysBetween(start, end) {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  if (s > e) return 0;
  let count = 0;
  const cur = new Date(s);
  cur.setHours(0, 0, 0, 0);
  const endDay = new Date(e);
  endDay.setHours(0, 0, 0, 0);
  while (cur < endDay) {
    cur.setDate(cur.getDate() + 1);
    const day = cur.getDay();
    const iso = cur.toISOString().slice(0, 10);
    if (day !== 0 && day !== 6 && !US_MARKET_HOLIDAYS.has(iso)) count++;
  }
  return count;
}

export function addTradingDays(fromDate, n) {
  const d = new Date(fromDate);
  d.setHours(0, 0, 0, 0);
  let added = 0;
  while (added < n) {
    d.setDate(d.getDate() + 1);
    const day = d.getDay();
    const iso = d.toISOString().slice(0, 10);
    if (day !== 0 && day !== 6 && !US_MARKET_HOLIDAYS.has(iso)) added++;
  }
  return d.toISOString();
}

export function formatLedgerDate(d = new Date()) {
  return d.toISOString().slice(0, 10);
}

export function formatLedgerTimeAmPm(d = new Date()) {
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });
}

export function formatLedgerTime24(d = new Date()) {
  return d.toTimeString().slice(0, 8);
}

export function classifyLedgerSignalType(result) {
  const bs = result?.buyScore;
  if (bs == null) return "WATCH";
  if (bs >= -3 && bs <= 3) return "CHOP";
  if (bs >= 4) return "CALLS";
  if (bs <= -4) return "PUTS";
  return "WATCH";
}

export function deriveLedgerPhase(result) {
  const ew = result?.earlyWarning;
  if (result?.callsSignal?.fullSignal || result?.putsSignal?.fullSignal) return "PHASE3";
  if (ew?.phase2Any) return "PHASE2";
  if (ew?.phase1Any) return "PHASE1";
  return null;
}

export function macdLedgerLabel(macd) {
  if (!macd) return "BEARISH";
  if (macd.crossover) return "CROSSOVER";
  if (macd.crossunder) return "CROSSUNDER";
  return macd.bullish ? "BULLISH" : "BEARISH";
}

export function sma200TrendingLabel(result) {
  if (result?.deathCross) return "DOWN";
  if (result?.goldenCross) return "UP";
  if (result?.sm50 != null && result?.sm200 != null) {
    return result.sm50 > result.sm200 ? "UP" : "DOWN";
  }
  return "MIXED";
}

const LEDGER_SECTOR_STAGE = {
  HOT: "HOT_SECTOR",
  ROTATING: "ROTATING_IN",
  ACCUMULATION: "ACCUMULATION",
  OUT: "ROTATING_OUT",
  NEUTRAL: "NEUTRAL",
  COOLING: "COOLING",
};

function ledgerSectorStage(stage) {
  if (!stage) return null;
  return LEDGER_SECTOR_STAGE[stage] || stage;
}

export function buildScanLedgerEntry(sym, result, verdict, opts = {}) {
  const now = new Date();
  const ticker = String(sym).toUpperCase();
  const bs = result?.buyScore ?? 0;
  const isPuts = bs <= -4;
  const atr = result?.atr;
  const price = result?.price;
  const stopPrice = isPuts
    ? (atr && price ? price + 2 * atr : null)
    : (atr && price ? price - 2 * atr : null);

  return {
    id: `${ticker}_${Date.now()}`,
    ticker,
    date: formatLedgerDate(now),
    time: formatLedgerTimeAmPm(now),
    scanned_at: now.toISOString(),
    price: typeof price === "number" ? price : null,
    score: typeof result?.buyScore === "number" ? result.buyScore : null,
    verdict: verdict?.label ?? result?.signal ?? "",
    action: verdict?.action ?? "",
    signal: opts.signalAction ?? result?.signal ?? "",
    signal_type: classifyLedgerSignalType(result),
    setup_type: result?.activeSetupType && result.activeSetupType !== "MOMENTUM" ? result.activeSetupType : null,
    phase: deriveLedgerPhase(result),
    cmf: result?.cmf ?? null,
    rsi: result?.rsi ?? null,
    macd: macdLedgerLabel(result?.macd),
    ema21_slope: result?.ema21SlopeLabel || "",
    sma200_trending: sma200TrendingLabel(result),
    rvol: result?.rvol?.ratio ?? null,
    rs_vs_spy: result?.rs?.spread ?? null,
    market_tide: typeof result?.marketTide?.breadthScore === "number"
      ? result.marketTide.breadthScore
      : (typeof result?.marketTide === "number" ? result.marketTide : 0),
    macro_score: typeof opts.macroScore === "number" ? opts.macroScore : 0,
    week52_position: result?.range52w?.score ?? null,
    earnings_days: result?.earningsState?.daysAway ?? result?.earningsDaysAway ?? null,
    t1_price: isPuts ? (result?.d1 ?? null) : (result?.t1 ?? null),
    t2_price: isPuts ? (result?.d2 ?? null) : (result?.t2 ?? null),
    stop_price: stopPrice,
    sector: opts.sector ?? result?.sector ?? null,
    sector_stage: ledgerSectorStage(opts.sectorStage ?? result?.sectorStage),
    sector_etf: opts.sectorEtf ?? result?.sectorEtf ?? null,
    sector_vs_spy_5d: opts.sectorVsSpy5d ?? result?.sectorVsSpy5d ?? null,
  };
}

function entryTimestamp(e) {
  if (e?.scanned_at) return Date.parse(e.scanned_at);
  if (e?.date) return Date.parse(`${e.date}T12:00:00`);
  return 0;
}

export { entryTimestamp };

export function findRecentLedgerEntry(list, ticker, withinMs = DEDUPE_MS) {
  const sym = String(ticker).toUpperCase();
  const now = Date.now();
  let best = null;
  let bestTs = 0;
  for (const e of list || []) {
    if ((e.ticker || "").toUpperCase() !== sym) continue;
    const ts = entryTimestamp(e);
    if (now - ts <= withinMs && ts >= bestTs) {
      best = e;
      bestTs = ts;
    }
  }
  return best;
}

export function findPreviousLedgerEntry(list, ticker, excludeId = null) {
  const sym = String(ticker).toUpperCase();
  const matches = (list || [])
    .filter(e => (e.ticker || "").toUpperCase() === sym && e.id !== excludeId)
    .sort((a, b) => entryTimestamp(b) - entryTimestamp(a));
  return matches[0] ?? null;
}

export function capScanLedger(list) {
  const arr = Array.isArray(list) ? [...list] : [];
  const cutoff = Date.now() - 30 * 86400000;
  const protectedIds = new Set();
  const byTicker = {};
  for (const e of arr) {
    const t = (e.ticker || "").toUpperCase();
    if (!byTicker[t]) byTicker[t] = [];
    byTicker[t].push(e);
  }
  for (const entries of Object.values(byTicker)) {
    for (const e of entries) {
      if (entryTimestamp(e) >= cutoff) protectedIds.add(e.id);
    }
  }
  let capped = arr.sort((a, b) => entryTimestamp(b) - entryTimestamp(a));
  while (capped.length > SCAN_LEDGER_CAP) {
    const idx = capped.findIndex(e => !protectedIds.has(e.id));
    if (idx < 0) break;
    capped.splice(idx, 1);
  }
  return capped.slice(0, SCAN_LEDGER_CAP);
}

export function mergeScanLedgerEntry(existing, entry) {
  const list = Array.isArray(existing) ? [...existing] : [];
  const recent = findRecentLedgerEntry(list, entry.ticker, DEDUPE_MS);
  if (recent) {
    const idx = list.findIndex(e => e.id === recent.id);
    const merged = { ...recent, ...entry, id: recent.id };
    list[idx] = merged;
    return { list: capScanLedger(list), entry: merged, updated: true, previous: recent };
  }
  list.unshift(entry);
  return { list: capScanLedger(list), entry, updated: false, previous: findPreviousLedgerEntry(list, entry.ticker, entry.id) };
}

export function detectChangedIndicators(prev, curr) {
  const changes = [];
  if (!prev || !curr) return changes;
  if (prev.cmf != null && curr.cmf != null) {
    if (prev.cmf <= 0 && curr.cmf > 0) changes.push("CMF turned positive");
    if (prev.cmf >= 0 && curr.cmf < 0) changes.push("CMF turned negative");
    if (prev.cmf > -0.10 && curr.cmf <= -0.10) changes.push("CMF dropped below -0.10");
  }
  if (prev.rsi != null && curr.rsi != null) {
    if (prev.rsi >= 40 && curr.rsi < 40) changes.push("RSI dropped below 40");
    if (prev.rsi < 50 && curr.rsi >= 50) changes.push("RSI crossed above 50");
    if (prev.rsi <= 30 && curr.rsi > 30) changes.push("RSI recovered above 30");
  }
  if (prev.ema21_slope !== curr.ema21_slope) {
    if (String(curr.ema21_slope).includes("Falling")) changes.push("EMA21 slope turned falling");
    if (String(curr.ema21_slope).includes("Rising")) changes.push("EMA21 slope turned rising");
  }
  if (prev.score != null && curr.score != null) {
    if (prev.score >= 0 && curr.score < 0) changes.push("Score crossed below 0");
    if (prev.score < 0 && curr.score >= 0) changes.push("Score crossed above 0");
  }
  return changes;
}

export function buildSignalChangeEntry(ticker, prev, curr, changedIndicators) {
  const now = new Date();
  return {
    id: `${String(ticker).toUpperCase()}_${Date.now()}`,
    ticker: String(ticker).toUpperCase(),
    date: formatLedgerDate(now),
    time: formatLedgerTime24(now),
    from_verdict: prev?.verdict ?? "",
    to_verdict: curr?.verdict ?? "",
    from_score: prev?.score ?? null,
    to_score: curr?.score ?? null,
    from_signal: prev?.signal ?? prev?.signal_type ?? "",
    to_signal: curr?.signal ?? curr?.signal_type ?? "",
    price: curr?.price ?? null,
    changed_indicators: changedIndicators,
    market_tide: curr?.market_tide ?? 0,
  };
}

export function appendSignalChange(existing, change) {
  const list = Array.isArray(existing) ? [change, ...existing] : [change];
  return list.slice(0, SIGNAL_CHANGES_CAP);
}

export function shouldActivateActiveSignal(result) {
  const bs = result?.buyScore;
  return typeof bs === "number" && (bs >= 6 || bs <= -6);
}

const BUY_ENTRY_SIGNALS = new Set(["SWING_LONG", "BOTTOM_CONFIRMED"]);

function hadPriorBuySignal(active, inPosition = false) {
  if (inPosition) return true;
  const sig = active?.entry_signal;
  return BUY_ENTRY_SIGNALS.has(sig);
}

export function evaluateHardExit(active, result, previousLedgerEntry = null, inPosition = false) {
  if (!active || active.hard_exit_triggered) return null;
  if (!hadPriorBuySignal(active, inPosition)) return null;
  if (typeof result?.buyScore === "number" && result.buyScore >= 4) return null;
  const price = result?.price;
  if (price == null) return null;
  const isPuts = active.signal_type === "PUTS";

  if (active.stop_price != null) {
    if (!isPuts && price <= active.stop_price) return "Price hit stop level";
    if (isPuts && price >= active.stop_price) return "Price hit stop level";
  }
  const bs = result?.buyScore;
  if (typeof bs === "number") {
    if (!isPuts && bs < 0) return "Score dropped below 0";
    if (isPuts && bs > 0) return "Score rose above 0";
  }
  if (result.ema21 != null && hadPriorBuySignal(active, inPosition)) {
    if (!isPuts && price < result.ema21) return "EMA21 broken on close";
    if (isPuts && price > result.ema21) return "EMA21 broken on close";
  }
  return null;
}

export function processActiveSignals(activeList, sym, result, verdict, ledgerEntry, previousLedgerEntry = null) {
  const ticker = String(sym).toUpperCase();
  let list = Array.isArray(activeList) ? activeList.map(e => ({ ...e })) : [];
  let idx = list.findIndex(e => e.ticker === ticker);
  const inPosition = !!result?.inPosition;

  if (idx >= 0) {
    const bs = result?.buyScore;
    if (typeof bs === "number" && bs >= 4) {
      list[idx] = { ...list[idx], hard_exit_triggered: false, hard_exit_reason: null };
    } else {
      const reason = evaluateHardExit(list[idx], result, previousLedgerEntry, inPosition);
      if (reason) {
        list[idx] = { ...list[idx], hard_exit_triggered: true, hard_exit_reason: reason };
      }
    }
  }

  if (idx < 0 && shouldActivateActiveSignal(result)) {
    const bs = result.buyScore;
    const signal_type = bs >= 6 ? "CALLS" : "PUTS";
    const entry = {
      ticker,
      signal_type,
      entry_signal: result?.signal ?? null,
      entry_cmf: result?.cmf ?? null,
      signal_date: new Date().toISOString(),
      entry_score: bs,
      entry_price: result.price,
      entry_verdict: verdict?.label ?? "",
      setup_type: ledgerEntry?.setup_type ?? null,
      locked_until: addTradingDays(new Date(), ACTIVE_SIGNAL_TRADING_DAYS),
      hard_exit_triggered: false,
      hard_exit_reason: null,
      stop_price: ledgerEntry?.stop_price ?? null,
    };
    list.push(entry);
    idx = list.length - 1;
  }

  const active = idx >= 0 ? list[idx] : null;
  return { activeSignals: list, activeSignal: active };
}

export function buildActiveSignalDisplay(active, result) {
  if (!active) return null;
  const cur = result?.buyScore;
  if (active.hard_exit_triggered && typeof cur === "number" && cur >= 4) return null;
  const days = tradingDaysBetween(active.signal_date, new Date());
  const dayN = (days ?? 0) + 1;
  const ent = active.entry_score;
  const fmt = (n) => (n == null ? "—" : `${n > 0 ? "+" : ""}${n}`);
  if (active.hard_exit_triggered) {
    return {
      label: `⚠ EXIT SIGNAL — ${active.hard_exit_reason || "Hard exit"}`,
      scoreLine: `Entry: ${fmt(ent)} → Current: ${fmt(cur)}`,
      holdingDays: dayN,
      hardExit: true,
    };
  }
  return {
    label: `HOLDING · Day ${dayN}`,
    scoreLine: `Entry: ${fmt(ent)} → Current: ${fmt(cur)}`,
    holdingDays: dayN,
    hardExit: false,
  };
}

export function ledgerChangeFlag(prev, curr, hardExit) {
  if (hardExit) return { icon: "⚠", color: "#ef4444", title: "Hard exit triggered" };
  if (!prev) return { icon: "⚡", color: "#fbbf24", title: "New scan logged" };
  const ps = prev.score ?? 0;
  const cs = curr.score ?? 0;
  if ((ps < 6 && cs >= 6) || (ps > -6 && cs <= -6)) {
    return { icon: "⚡", color: "#fbbf24", title: "New signal fired" };
  }
  if (cs > ps) return { icon: "▲", color: "#22c55e", title: "Signal improved" };
  if (cs < ps) return { icon: "▼", color: "#ef4444", title: "Signal degraded" };
  if (prev.verdict !== curr.verdict) return { icon: "⚡", color: "#fbbf24", title: "Verdict changed" };
  return { icon: "↔", color: "#94a3b8", title: "No score change" };
}

export function summarizeTickerLedger(entries, activeSignal) {
  const sorted = [...entries].sort((a, b) => entryTimestamp(a) - entryTimestamp(b));
  const buyFires = sorted.filter(e => e.signal_type === "CALLS" && (e.score ?? 0) >= 6).length;
  const chopFlips = sorted.filter((e, i) => i > 0 && e.signal_type === "CHOP" && sorted[i - 1].signal_type === "CALLS").length;
  const scores = sorted.map(e => e.score).filter(v => typeof v === "number");
  let avgHold = null;
  if (scores.length >= 2) {
    const spans = [];
    for (let i = 1; i < sorted.length; i++) {
      const d = tradingDaysBetween(sorted[i - 1].scanned_at || sorted[i - 1].date, sorted[i].scanned_at || sorted[i].date);
      if (d != null) spans.push(d);
    }
    if (spans.length) avgHold = Math.round(spans.reduce((s, v) => s + v, 0) / spans.length);
  }
  const status = activeSignal?.hard_exit_triggered
    ? `EXIT — ${activeSignal.hard_exit_reason}`
    : activeSignal
    ? `HOLDING Day ${(tradingDaysBetween(activeSignal.signal_date, new Date()) ?? 0) + 1}`
    : sorted.length ? `Last: ${sorted[sorted.length - 1].verdict}` : "No scans";
  return {
    buyFires,
    chopFlips,
    avgHold,
    status,
    scanCount: sorted.length,
  };
}
